import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"

import type { MembershipApplicationStepId } from "../types"
import type { ApplicationCreateDTOGender } from "@/utils/api/membership-service/models"

export type MembershipApplicationDraftStatus = "draft" | "submitted" | "under-review"

export interface MembershipApplicationCategoryDraft {
  categoryId: number | null
  prerequisitesAccepted: boolean
}

export interface MembershipApplicationProfileDraft {
  firstName: string
  middleName?: string
  lastName: string
  email: string
  phoneNumber: string
  dateOfBirth: string
  gender?: ApplicationCreateDTOGender
  organisation: string
  profileLinkUrl?: string
}

export interface EmploymentHistoryDraftEntry {
  id: string
  organisation: string
  jobTitle: string
  startDate: string
  endDate?: string
  currentlyEmployed: boolean
  responsibilities?: string
  reportingTo?: string
}

export interface EducationHistoryDraftEntry {
  id: string
  institution: string
  qualification: string
  awardingBody: string
  graduationYear?: string
  fieldOfStudy?: string
  attachmentName?: string
  honors?: string
}

export interface AffiliationDraftEntry {
  id: string
  organisation: string
  role?: string
  membershipNumber?: string
  since?: string
  notes?: string
}

export interface AwardDraftEntry {
  id: string
  title: string
  issuer: string
  awardedOn?: string
  description?: string
}

export type SupportingDocumentStatus = "pending" | "uploaded"

export interface SupportingDocumentDraft {
  id: string
  documentType: string
  fileId?: string
  fileUrl?: string
  fileName?: string
  originalFilename?: string
  status: SupportingDocumentStatus
  notes?: string
  mandatory?: boolean
}

export type MembershipApplicationRequirementsDraft = Record<string, string | number | boolean | undefined>

export interface MembershipApplicationDraft {
  id: string
  status: MembershipApplicationDraftStatus
  completedStepIds: MembershipApplicationStepId[]
  lastSavedAt?: string
  category?: MembershipApplicationCategoryDraft
  profile?: MembershipApplicationProfileDraft
  requirements?: MembershipApplicationRequirementsDraft
  employmentHistory: EmploymentHistoryDraftEntry[]
  educationHistory: EducationHistoryDraftEntry[]
  professionalAffiliations: AffiliationDraftEntry[]
  professionalAwards: AwardDraftEntry[]
  supportingDocuments: SupportingDocumentDraft[]
}

export interface MembershipApplicationDraftUpdate {
  category?: MembershipApplicationCategoryDraft
  profile?: MembershipApplicationProfileDraft
  requirements?: MembershipApplicationRequirementsDraft
  employmentHistory?: EmploymentHistoryDraftEntry[]
  educationHistory?: EducationHistoryDraftEntry[]
  professionalAffiliations?: AffiliationDraftEntry[]
  professionalAwards?: AwardDraftEntry[]
  supportingDocuments?: SupportingDocumentDraft[]
  status?: MembershipApplicationDraftStatus
}

const STORAGE_KEY = "membershipApplicationDraft"

function clearDraftFromStorage() {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch (_error) {
    // ignore storage errors
  }
}

export const membershipApplicationDraftQueryKey = [
  "membershipApplication",
  "draft",
] as const

export const membershipApplicationDraftMutationKey = [
  "membershipApplication",
  "draft",
  "mutation",
] as const

const STEP_IDS: MembershipApplicationStepId[] = [
  "category",
  "profile",
  "requirements",
  "employment",
  "education",
  "affiliations",
  "documents",
  "review",
]

function generateId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch (_error) {
    // ignore
  }
  return Math.random().toString(36).slice(2, 12)
}

function createInitialDraft(): MembershipApplicationDraft {
  return {
    id: "draft-placeholder",
    status: "draft",
    completedStepIds: [],
    lastSavedAt: undefined,
    profile: undefined,
    requirements: undefined,
    employmentHistory: [],
    educationHistory: [],
    professionalAffiliations: [],
    professionalAwards: [],
    supportingDocuments: [],
  }
}


function sanitiseCategory(
  category: MembershipApplicationCategoryDraft | undefined,
): MembershipApplicationCategoryDraft | undefined {
  if (!category) return undefined
  return {
    categoryId:
      typeof category.categoryId === "number"
        ? category.categoryId
        : typeof category.categoryId === "string"
          ? Number.parseInt(category.categoryId, 10) || null
          : null,
    prerequisitesAccepted: Boolean(category.prerequisitesAccepted),
  }
}

function sanitiseProfile(
  profile: MembershipApplicationProfileDraft | undefined,
): MembershipApplicationProfileDraft | undefined {
  if (!profile) return undefined
  return {
    firstName: (profile.firstName ?? "").toString().trim(),
    middleName: profile.middleName?.toString().trim() || undefined,
    lastName: (profile.lastName ?? "").toString().trim(),
    email: (profile.email ?? "").toString().trim(),
    phoneNumber: (profile.phoneNumber ?? "").toString().trim(),
    dateOfBirth: (profile.dateOfBirth ?? "").toString(),
    gender: profile.gender,
    organisation: (profile.organisation ?? "").toString().trim(),
    profileLinkUrl: profile.profileLinkUrl?.toString().trim() || undefined,
  }
}

function sanitiseEmploymentEntries(
  entries: EmploymentHistoryDraftEntry[] | undefined,
): EmploymentHistoryDraftEntry[] {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    id: entry.id || generateId(),
    organisation: (entry.organisation ?? "").toString().trim(),
    jobTitle: (entry.jobTitle ?? "").toString().trim(),
    startDate: (entry.startDate ?? "").toString(),
    endDate: entry.endDate ? entry.endDate.toString() : undefined,
    currentlyEmployed: Boolean(entry.currentlyEmployed),
    responsibilities: entry.responsibilities?.toString().trim() || undefined,
    reportingTo: entry.reportingTo?.toString().trim() || undefined,
  }))
}

function sanitiseEducationEntries(
  entries: EducationHistoryDraftEntry[] | undefined,
): EducationHistoryDraftEntry[] {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    id: entry.id || generateId(),
    institution: (entry.institution ?? "").toString().trim(),
    qualification: (entry.qualification ?? "").toString().trim(),
    awardingBody: (entry.awardingBody ?? "").toString().trim(),
    graduationYear: entry.graduationYear?.toString() || undefined,
    fieldOfStudy: entry.fieldOfStudy?.toString().trim() || undefined,
    attachmentName: entry.attachmentName?.toString().trim() || undefined,
    honors: entry.honors?.toString().trim() || undefined,
  }))
}

function sanitiseAffiliations(entries: AffiliationDraftEntry[] | undefined): AffiliationDraftEntry[] {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    id: entry.id || generateId(),
    organisation: (entry.organisation ?? "").toString().trim(),
    role: entry.role?.toString().trim() || undefined,
    membershipNumber: entry.membershipNumber?.toString().trim() || undefined,
    since: entry.since?.toString() || undefined,
    notes: entry.notes?.toString().trim() || undefined,
  }))
}

function sanitiseAwards(entries: AwardDraftEntry[] | undefined): AwardDraftEntry[] {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    id: entry.id || generateId(),
    title: (entry.title ?? "").toString().trim(),
    issuer: (entry.issuer ?? "").toString().trim(),
    awardedOn: entry.awardedOn?.toString() || undefined,
    description: entry.description?.toString().trim() || undefined,
  }))
}

function sanitiseDocuments(entries: SupportingDocumentDraft[] | undefined): SupportingDocumentDraft[] {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    id: entry.id || generateId(),
    documentType: (entry.documentType ?? "").toString().trim(),
    fileId: entry.fileId?.toString().trim(),
    fileUrl: entry.fileUrl?.toString().trim() || undefined,
    fileName: entry.fileName?.toString().trim() || undefined,
    originalFilename: entry.originalFilename?.toString().trim() || entry.fileName?.toString().trim() || undefined,
    status: entry.status === "uploaded" ? "uploaded" : "pending",
    notes: entry.notes?.toString().trim() || undefined,
    mandatory: typeof entry.mandatory === "boolean" ? entry.mandatory : undefined,
  }))
}

function sanitiseRequirements(
  requirements: MembershipApplicationRequirementsDraft | undefined,
): MembershipApplicationRequirementsDraft | undefined {
  if (!requirements || typeof requirements !== "object") return undefined
  return { ...requirements }
}

function isCategoryComplete(category?: MembershipApplicationCategoryDraft): boolean {
  if (!category) return false
  if (!category.categoryId) return false
  if (!category.prerequisitesAccepted) return false
  
  return true
}

function isProfileComplete(profile?: MembershipApplicationProfileDraft): boolean {
  if (!profile) return false
  const requiredStrings = [
    profile.firstName,
    profile.lastName,
    profile.email,
    profile.phoneNumber,
    profile.dateOfBirth,
    profile.organisation,
  ]
  return requiredStrings.every((value) => typeof value === "string" && value.trim().length > 0)
}

function isRequirementsComplete(requirements?: MembershipApplicationRequirementsDraft): boolean {
  if (!requirements) return false
  return Object.values(requirements).some((value) => {
    if (typeof value === "string") {
      return value.trim().length > 0
    }
    if (typeof value === "number") {
      return true
    }
    if (typeof value === "boolean") {
      return true
    }
    return false
  }
  )
}

function isEmploymentComplete(entries: EmploymentHistoryDraftEntry[]): boolean {
  if (entries.length === 0) return false
  return entries.every(
    (entry) =>
      entry.organisation.length > 0 &&
      entry.jobTitle.length > 0 &&
      entry.startDate.length > 0 &&
      (entry.currentlyEmployed || (entry.endDate && entry.endDate.length > 0)),
  )
}

function isEducationComplete(entries: EducationHistoryDraftEntry[]): boolean {
  if (entries.length === 0) return false
  return entries.every(
    (entry) =>
      entry.institution.length > 0 &&
      entry.qualification.length > 0 &&
      entry.awardingBody.length > 0,
  )
}

function isAffiliationsComplete(affiliations: AffiliationDraftEntry[], awards: AwardDraftEntry[]): boolean {
  return (
    affiliations.some((entry) => entry.organisation.length > 0) ||
    awards.some((entry) => entry.title.length > 0)
  )
}

function isDocumentsComplete(entries: SupportingDocumentDraft[]): boolean {
  if (entries.length === 0) return false
  return entries.every((entry) => entry.documentType.length > 0 && entry.status === "uploaded")
}

function computeCompletedSteps(draft: MembershipApplicationDraft): MembershipApplicationStepId[] {
  const completed = new Set<MembershipApplicationStepId>()
  
  if (isCategoryComplete(draft.category)) completed.add("category")
  if (isProfileComplete(draft.profile)) completed.add("profile")
  if (isRequirementsComplete(draft.requirements)) completed.add("requirements")
  if (isEmploymentComplete(draft.employmentHistory)) completed.add("employment")
  if (isEducationComplete(draft.educationHistory)) completed.add("education")
  if (isAffiliationsComplete(draft.professionalAffiliations, draft.professionalAwards)) {
    completed.add("affiliations")
  }
  if (isDocumentsComplete(draft.supportingDocuments)) completed.add("documents")
  if (draft.status === "submitted" || draft.status === "under-review") {
    completed.add("review")
  }
  return STEP_IDS.filter((step) => completed.has(step))
}

function loadDraftFromStorage(): MembershipApplicationDraft {
  if (typeof window === "undefined") {
    return createInitialDraft()
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return createInitialDraft()
    }
    const parsed = JSON.parse(raw) as Partial<MembershipApplicationDraft>

    const draft: MembershipApplicationDraft = {
      ...createInitialDraft(),
      ...parsed,
      id: parsed.id ?? "draft-placeholder",
      status: parsed.status ?? "draft",
      category: sanitiseCategory(parsed.category),
      profile: sanitiseProfile(parsed.profile),
      requirements: sanitiseRequirements(parsed.requirements),
      employmentHistory: sanitiseEmploymentEntries(parsed.employmentHistory),
      educationHistory: sanitiseEducationEntries(parsed.educationHistory),
      professionalAffiliations: sanitiseAffiliations(parsed.professionalAffiliations),
      professionalAwards: sanitiseAwards(parsed.professionalAwards),
      supportingDocuments: sanitiseDocuments(parsed.supportingDocuments),
    }

    draft.completedStepIds = computeCompletedSteps(draft)
    return draft
  } catch (_error) {
    return createInitialDraft()
  }
}

function persistDraftToStorage(draft: MembershipApplicationDraft) {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch (_error) {
    // Ignore storage errors (quota, privacy modes, etc.)
  }
}

function applyDraftUpdate(
  previous: MembershipApplicationDraft | undefined,
  update: MembershipApplicationDraftUpdate,
): MembershipApplicationDraft {
  if (update.status === "submitted") {
    clearDraftFromStorage()
    return createInitialDraft()
  }

  const base = previous ?? loadDraftFromStorage()
  
  const next: MembershipApplicationDraft = {
    ...base,
    completedStepIds: [...base.completedStepIds],
  }

  let wasMutated = false

  if (update.category) {
    next.category = sanitiseCategory(update.category)
    wasMutated = true
  }

  if (update.profile) {
    next.profile = sanitiseProfile(update.profile)
    wasMutated = true
  }

  if (update.requirements) {
    next.requirements = sanitiseRequirements(update.requirements)
    wasMutated = true
  }

  if (update.employmentHistory) {
    next.employmentHistory = sanitiseEmploymentEntries(update.employmentHistory)
    wasMutated = true
  }

  if (update.educationHistory) {
    next.educationHistory = sanitiseEducationEntries(update.educationHistory)
    wasMutated = true
  }

  if (update.professionalAffiliations) {
    next.professionalAffiliations = sanitiseAffiliations(update.professionalAffiliations)
    wasMutated = true
  }

  if (update.professionalAwards) {
    next.professionalAwards = sanitiseAwards(update.professionalAwards)
    wasMutated = true
  }

  if (update.supportingDocuments) {
    next.supportingDocuments = sanitiseDocuments(update.supportingDocuments)
    wasMutated = true
  }

  if (update.status) {
    next.status = update.status
    wasMutated = true
  }

  if (wasMutated) {
    next.completedStepIds = computeCompletedSteps(next)
    next.lastSavedAt = new Date().toISOString()
    persistDraftToStorage(next)
  }

  return next
}

async function fetchMembershipApplicationDraft(): Promise<MembershipApplicationDraft> {
  return loadDraftFromStorage()
}

export function useMembershipApplicationDraft(options?: { enabled?: boolean }) {
  const { enabled = true } = options ?? {}

  return useQuery({
    queryKey: membershipApplicationDraftQueryKey,
    queryFn: fetchMembershipApplicationDraft,
    staleTime: 30_000,
    gcTime: Infinity,
    enabled,
  })
}

export function useSaveMembershipApplicationDraft() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: membershipApplicationDraftMutationKey,
    mutationFn: async (update: MembershipApplicationDraftUpdate) => update,
    onSuccess: (update) => {
      queryClient.setQueryData(membershipApplicationDraftQueryKey, (previous) =>
        applyDraftUpdate(previous as MembershipApplicationDraft | undefined, update),
      )
    },
  })
}

export function useIsSavingMembershipApplicationDraft() {
  const inFlight = useIsMutating({ mutationKey: membershipApplicationDraftMutationKey })
  return useMemo(() => inFlight > 0, [inFlight])
}
